import React, { useState, useEffect } from 'react';
import { Save, Eye, Palette, Type, Image, Phone, MapPin, Settings } from 'lucide-react';
import { contentAPI, handleAPIError } from '../services/api';
import ColorPicker from '../components/ContentEditor/ColorPicker';
import TextEditor from '../components/ContentEditor/TextEditor';
import ImageUploader from '../components/ContentEditor/ImageUploader';
import { LoadingPage } from '../components/LoadingSpinner';

const ContentEditorPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');
  const [contentData, setContentData] = useState({});

  const tabs = [
    { id: 'hero', label: 'Hero Section', icon: Type },
    { id: 'hero-images', label: 'Hero Images', icon: Image },
    { id: 'services', label: 'Services', icon: Settings },
    { id: 'contact', label: 'Contact Info', icon: Phone },
    { id: 'colors', label: 'Colours & Theme', icon: Palette },
    { id: 'testimonial', label: 'Testimonial', icon: Type },
  ];

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await contentAPI.getAllContent();
      
      // Convert array to object for easier access
      const contentMap = {};
      data.forEach(item => {
        contentMap[item.section] = item.content;
      });
      
      setContentData(contentMap);
    } catch (err) {
      setError(handleAPIError(err, 'Failed to load content'));
    } finally {
      setLoading(false);
    }
  };

  const updateContent = (section, field, value) => {
    setContentData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateServiceContent = (section, serviceIndex, field, value) => {
    setContentData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        services: prev[section]?.services?.map((service, index) => 
          index === serviceIndex 
            ? { ...service, [field]: value }
            : service
        ) || []
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Save each section
      for (const [section, content] of Object.entries(contentData)) {
        if (content && Object.keys(content).length > 0) {
          await contentAPI.updateContentSection(section, content);
        }
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(handleAPIError(err, 'Failed to save content'));
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    // Open homepage in new tab for preview
    window.open('/', '_blank');
  };

  if (loading) {
    return <LoadingPage message="Loading content editor..." />;
  }

  const renderHeroEditor = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-black font-inter">Hero Section Content</h3>
      
      <ImageUploader
        label="Hero Background Image"
        value={contentData.hero?.backgroundImage}
        onChange={(value) => updateContent('hero', 'backgroundImage', value)}
        helpText="Upload a background image for your hero section. Leave empty for solid color background."
      />
      
      {contentData.hero?.backgroundImage && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-bold text-gray-700 font-inter mb-3">Image Overlay Settings</h4>
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="darkOverlay"
              checked={contentData.hero?.darkOverlay || false}
              onChange={(e) => updateContent('hero', 'darkOverlay', e.target.checked)}
              className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
            />
            <label htmlFor="darkOverlay" className="text-sm font-medium text-gray-700">
              Enable dark overlay for better text readability
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Adds a dark semi-transparent layer over the background image to improve text contrast
          </p>
        </div>
      )}
      
      <TextEditor
        label="Main Title"
        value={contentData.hero?.title}
        onChange={(value) => updateContent('hero', 'title', value)}
        placeholder="YOUR NO.1"
        maxLength={50}
        helpText="The big headline at the top of your homepage"
      />
      
      <TextEditor
        label="Subtitle"
        value={contentData.hero?.subtitle}
        onChange={(value) => updateContent('hero', 'subtitle', value)}
        placeholder="COMMERCIAL VEHICLE SPECIALIST"
        maxLength={100}
        helpText="The text that appears below your main title"
      />
      
      <TextEditor
        label="Description"
        value={contentData.hero?.description}
        onChange={(value) => updateContent('hero', 'description', value)}
        placeholder="Over 30 years of experience..."
        multiline={true}
        rows={4}
        maxLength={300}
        helpText="The paragraph text that explains your business"
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TextEditor
          label="Primary Button Text"
          value={contentData.hero?.ctaText}
          onChange={(value) => updateContent('hero', 'ctaText', value)}
          placeholder="Browse Our Trucks"
          maxLength={30}
        />
        
        <TextEditor
          label="Secondary Button Text"
          value={contentData.hero?.ctaSecondaryText}
          onChange={(value) => updateContent('hero', 'ctaSecondaryText', value)}
          placeholder="Call Now"
          maxLength={30}
        />
      </div>
    </div>
  );

  const renderServicesEditor = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-black font-inter">Services Section</h3>
      
      <TextEditor
        label="Services Title"
        value={contentData.services?.title}
        onChange={(value) => updateContent('services', 'title', value)}
        placeholder="Our Services"
        maxLength={50}
      />
      
      <TextEditor
        label="Services Subtitle"
        value={contentData.services?.subtitle}
        onChange={(value) => updateContent('services', 'subtitle', value)}
        placeholder="Comprehensive commercial vehicle solutions..."
        multiline={true}
        rows={2}
        maxLength={200}
      />
      
      <div className="space-y-4">
        <h4 className="font-bold text-gray-700 font-inter">Service Items</h4>
        {contentData.services?.services?.map((service, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3">
            <TextEditor
              label={`Service ${index + 1} - Title`}
              value={service.title}
              onChange={(value) => updateServiceContent('services', index, 'title', value)}
              placeholder="SALES"
              maxLength={20}
            />
            <TextEditor
              label={`Service ${index + 1} - Description`}
              value={service.description}
              onChange={(value) => updateServiceContent('services', index, 'description', value)}
              placeholder="Quality used commercial vehicles..."
              multiline={true}
              rows={2}
              maxLength={150}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderContactEditor = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-black font-inter">Contact Information</h3>
      
      <TextEditor
        label="Company Name"
        value={contentData.contact?.company_name}
        onChange={(value) => updateContent('contact', 'company_name', value)}
        placeholder="Lawman Commercials"
        maxLength={50}
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TextEditor
          label="Office Phone"
          value={contentData.contact?.office_phone}
          onChange={(value) => updateContent('contact', 'office_phone', value)}
          placeholder="01530 231 235"
          type="tel"
        />
        
        <TextEditor
          label="Mobile Phone (24/7)"
          value={contentData.contact?.mobile_phone}
          onChange={(value) => updateContent('contact', 'mobile_phone', value)}
          placeholder="07718 738 785"
          type="tel"
        />
      </div>
      
      <TextEditor
        label="Email Address"
        value={contentData.contact?.email}
        onChange={(value) => updateContent('contact', 'email', value)}
        placeholder="info@lawmancommercials.com"
        type="email"
      />
      
      <TextEditor
        label="Business Address"
        value={contentData.contact?.address}
        onChange={(value) => updateContent('contact', 'address', value)}
        placeholder="31 Merrylees Industrial Estate..."
        multiline={true}
        rows={2}
        maxLength={200}
      />
    </div>
  );

  const renderColorsEditor = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-black font-inter">Colours & Theme</h3>
      <p className="text-gray-600 font-inter">
        Customise your website's colour scheme. Changes will apply across the entire site.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ColorPicker
          label="Primary Colour (Black Elements)"
          value={contentData.colours?.primary}
          onChange={(value) => updateContent('colours', 'primary', value)}
        />
        
        <ColorPicker
          label="Accent Colour (Orange Elements)"
          value={contentData.colours?.accent}
          onChange={(value) => updateContent('colours', 'accent', value)}
        />
        
        <ColorPicker
          label="Background Colour"
          value={contentData.colours?.background}
          onChange={(value) => updateContent('colours', 'background', value)}
        />
        
        <ColorPicker
          label="Text Colour"
          value={contentData.colours?.text}
          onChange={(value) => updateContent('colours', 'text', value)}
        />
      </div>

      {/* Navigation Background Colour with Grey Swatches */}
      <div className="mt-8">
        <h4 className="text-lg font-bold text-black font-inter mb-4">Navigation Background</h4>
        <p className="text-gray-600 font-inter mb-4">
          Choose a background colour for the navigation bar. Lighter colours will show more of the glass effect.
        </p>
        
        <div className="space-y-4">
          <div className="grid grid-cols-8 gap-2">
            {[
              { color: '#ffffff', label: 'White' },
              { color: '#f8f9fa', label: 'Light Grey 1' },
              { color: '#e9ecef', label: 'Light Grey 2' },
              { color: '#dee2e6', label: 'Light Grey 3' },
              { color: '#ced4da', label: 'Grey' },
              { color: '#adb5bd', label: 'Dark Grey 1' },
              { color: '#6c757d', label: 'Dark Grey 2' },
              { color: '#495057', label: 'Dark Grey 3' },
              { color: '#343a40', label: 'Dark Grey 4' },
              { color: '#212529', label: 'Very Dark Grey' },
              { color: '#2A2A2A', label: 'Current Dark' },
              { color: '#000000', label: 'Black' }
            ].map((swatch) => (
              <button
                key={swatch.color}
                className={`w-12 h-12 rounded-lg border-2 transition-all hover:scale-110 ${
                  (contentData.colours?.navigationBackground || '#2A2A2A') === swatch.color 
                    ? 'border-orange-500 ring-2 ring-orange-200' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                style={{ backgroundColor: swatch.color }}
                onClick={() => updateContent('colours', 'navigationBackground', swatch.color)}
                title={swatch.label}
              />
            ))}
          </div>
          
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-700">Current Colour:</span>
            <div 
              className="w-8 h-8 rounded border border-gray-300"
              style={{ backgroundColor: contentData.colours?.navigationBackground || '#2A2A2A' }}
            ></div>
            <span className="text-sm text-gray-600">
              {contentData.colours?.navigationBackground || '#2A2A2A'}
            </span>
          </div>
        </div>
      </div>
      
      {/* Colour Preview */}
      <div className="mt-8 p-6 rounded-lg border border-gray-200">
        <h4 className="font-bold mb-4 font-inter">Colour Preview</h4>
        <div className="space-y-3">
          <div 
            className="p-4 rounded-lg"
            style={{ 
              backgroundColor: contentData.colours?.primary || '#000000',
              color: contentData.colours?.background || '#ffffff'
            }}
          >
            <h5 className="font-bold">Primary Background Sample</h5>
            <p>This is how text appears on primary backgrounds</p>
          </div>
          <div 
            className="p-4 rounded-lg"
            style={{ 
              backgroundColor: contentData.colours?.accent || '#ea6c0a',
              color: contentData.colours?.background || '#ffffff'
            }}
          >
            <h5 className="font-bold">Accent Background Sample</h5>
            <p>This is how buttons and highlights will look</p>
          </div>
          <div 
            className="p-4 rounded-lg backdrop-blur-sm"
            style={{ 
              backgroundColor: `${contentData.colours?.navigationBackground || '#2A2A2A'}CC`,
              color: '#ffffff'
            }}
          >
            <h5 className="font-bold">Navigation Glass Effect Preview</h5>
            <p>This is how the navigation will look with glass-morphism effect</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTestimonialEditor = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-black font-inter">Customer Testimonial</h3>
      
      <TextEditor
        label="Testimonial Text"
        value={contentData.testimonial?.text}
        onChange={(value) => updateContent('testimonial', 'text', value)}
        placeholder="Top staff and very competitive prices."
        multiline={true}
        rows={3}
        maxLength={300}
        helpText="The customer review quote"
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TextEditor
          label="Customer Name"
          value={contentData.testimonial?.author}
          onChange={(value) => updateContent('testimonial', 'author', value)}
          placeholder="Adrian M."
          maxLength={50}
        />
        
        <TextEditor
          label="Review Source"
          value={contentData.testimonial?.source}
          onChange={(value) => updateContent('testimonial', 'source', value)}
          placeholder="Google Reviews"
          maxLength={50}
        />
      </div>
      
      <div>
        <label className="block text-sm font-bold text-gray-700 font-inter mb-2">
          Star Rating (1-5)
        </label>
        <select
          value={contentData.testimonial?.rating || 5}
          onChange={(e) => updateContent('testimonial', 'rating', parseInt(e.target.value))}
          className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
        >
          {[1, 2, 3, 4, 5].map(rating => (
            <option key={rating} value={rating}>{rating} Star{rating !== 1 ? 's' : ''}</option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderHeroImagesEditor = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-black font-inter">Hero Background Images</h3>
      <p className="text-gray-600 text-sm">
        Manage hero background images for different pages. Upload high-quality images (recommended: 1920x800px or larger) for best results.
      </p>
      
      {/* Contact Page Hero Image */}
      <div className="border border-gray-200 rounded-lg p-6 space-y-4">
        <h4 className="font-bold text-gray-700 font-inter flex items-center gap-2">
          <Phone className="h-5 w-5" />
          Contact Page Hero Image
        </h4>
        <ImageUploader
          label="Background Image"
          value={contentData.heroImages?.contactPage}
          onChange={(value) => updateContent('heroImages', 'contactPage', value)}
          helpText="Background image for the Contact Us page hero section"
        />
        {contentData.heroImages?.contactPage && (
          <div className="mt-4">
            <img 
              src={contentData.heroImages.contactPage} 
              alt="Contact page hero preview" 
              className="w-full h-32 object-cover rounded border"
            />
          </div>
        )}
      </div>

      {/* Repairs Page Hero Image */}
      <div className="border border-gray-200 rounded-lg p-6 space-y-4">
        <h4 className="font-bold text-gray-700 font-inter flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Repairs Page Hero Image
        </h4>
        <ImageUploader
          label="Background Image"
          value={contentData.heroImages?.repairsPage}
          onChange={(value) => updateContent('heroImages', 'repairsPage', value)}
          helpText="Background image for the Repairs page hero section"
        />
        {contentData.heroImages?.repairsPage && (
          <div className="mt-4">
            <img 
              src={contentData.heroImages.repairsPage} 
              alt="Repairs page hero preview" 
              className="w-full h-32 object-cover rounded border"
            />
          </div>
        )}
      </div>

      {/* Parts Page Hero Image */}
      <div className="border border-gray-200 rounded-lg p-6 space-y-4">
        <h4 className="font-bold text-gray-700 font-inter flex items-center gap-2">
          <Image className="h-5 w-5" />
          Parts Page Hero Image
        </h4>
        <ImageUploader
          label="Background Image"
          value={contentData.heroImages?.partsPage}
          onChange={(value) => updateContent('heroImages', 'partsPage', value)}
          helpText="Background image for the Parts page hero section"
        />
        {contentData.heroImages?.partsPage && (
          <div className="mt-4">
            <img 
              src={contentData.heroImages.partsPage} 
              alt="Parts page hero preview" 
              className="w-full h-32 object-cover rounded border"
            />
          </div>
        )}
      </div>

      {/* About Page Hero Image */}
      <div className="border border-gray-200 rounded-lg p-6 space-y-4">
        <h4 className="font-bold text-gray-700 font-inter flex items-center gap-2">
          <Type className="h-5 w-5" />
          About Page Hero Image
        </h4>
        <ImageUploader
          label="Background Image"
          value={contentData.heroImages?.aboutPage}
          onChange={(value) => updateContent('heroImages', 'aboutPage', value)}
          helpText="Background image for the About Us page hero section"
        />
        {contentData.heroImages?.aboutPage && (
          <div className="mt-4">
            <img 
              src={contentData.heroImages.aboutPage} 
              alt="About page hero preview" 
              className="w-full h-32 object-cover rounded border"
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-black text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-black font-inter">
                Content <span className="text-orange-500">Editor</span>
              </h1>
              <p className="text-gray-300 mt-2 font-inter">
                Customise your website content and appearance
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handlePreview}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-colors"
              >
                <Eye className="h-4 w-4" />
                Preview
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 transition-colors"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600 font-inter">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-600 font-inter">
              ✅ Content saved successfully! Changes are now live on your website.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Tabs */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-4">
              <h3 className="font-bold text-gray-800 mb-4 font-inter">Edit Sections</h3>
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        activeTab === tab.id
                          ? 'bg-orange-600 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="font-inter text-sm">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Content Editor */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-lg p-8">
              {activeTab === 'hero' && renderHeroEditor()}
              {activeTab === 'hero-images' && renderHeroImagesEditor()}
              {activeTab === 'services' && renderServicesEditor()}
              {activeTab === 'contact' && renderContactEditor()}
              {activeTab === 'colors' && renderColorsEditor()}
              {activeTab === 'testimonial' && renderTestimonialEditor()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentEditorPage;